import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { TrainedModelProviderInstance } from "../@shared/services/TrainedModelProvider";
import { NotificationModel, NotificationModelType } from "../reducers/main/mainModel";
import { onThrowAlert, loadOpenedModel } from "../sagas/dataSaga/dataSagaActions";


export type UseModelProps = {

}

export type UseModelResult = {
    onRetrainModel: (id: string, isForce: boolean) => void;
    onRecalculateMetrics: (id: string) => void;
    onCancelTraining: (id: string) => void;
    isRetrainConfirmOpen: boolean;
    setIsRetrainConfirmOpen: (v: boolean) => void;
    isRetrainingInProgress: boolean;
    isCancelConfirmOpen: boolean;
    setIsCancelConfirmOpen: (v: boolean) => void;
    isCancellationInProgress: boolean;
}

export const useModel = () => {
    const dispatch = useDispatch();
    const [isRetrainConfirmOpen, setIsRetrainConfirmOpen] = useState(false);
    const [isRetrainingInProgress, setIsRetrainingInProgress] = useState(false);
    const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
    const [isCancellationInProgress, setIsCancellationInProgress] = useState(false);

    const onRetrainModel = useCallback(async (id: string, isForce: boolean) => {
        try {
            setIsRetrainingInProgress(true);
            const result = await TrainedModelProviderInstance.retrain({ id: id, force: isForce });
            if (result.status !== 200) {
                dispatch(onThrowAlert({
                    message: result.data.toString(),
                    lifeTime: 10000
                } as NotificationModel));
            }
            else {
                dispatch(loadOpenedModel(id));
                dispatch(onThrowAlert({
                    message: "Model added to the queue for training",
                    lifeTime: 3000,
                    type: NotificationModelType.Success
                } as NotificationModel));
            }
        }
        catch (error) {
            dispatch(onThrowAlert({
                message: (error as Error).message,
                lifeTime: 10000
            } as NotificationModel));
        }
        finally {
            setIsRetrainConfirmOpen(false);
            setIsRetrainingInProgress(false);
        }
    }, [dispatch])

    const onRecalculateMetrics = useCallback(async (id: string) => {
        try {
            const result = await TrainedModelProviderInstance.recalculateMetrics({ id: id });
            if (result.status !== 200) {
                dispatch(onThrowAlert({
                    message: result.data.toString(),
                    lifeTime: 10000
                } as NotificationModel));
            }
            else {
                dispatch(loadOpenedModel(id));
                dispatch(onThrowAlert({
                    message: "Model added to the queue for metric recalculation",
                    lifeTime: 3000,
                    type: NotificationModelType.Success
                } as NotificationModel));
            }
        }
        catch (error) {
            dispatch(onThrowAlert({
                message: (error as Error).message,
                lifeTime: 10000
            } as NotificationModel));
        }
        finally {
            setIsRetrainConfirmOpen(false);
        }
    }, [dispatch])

    const onCancelTraining = useCallback(async (id: string) => {
        try {
            setIsCancellationInProgress(true);
            const result = await TrainedModelProviderInstance.cancel(id);
            if (result.status !== 200) {
                dispatch(onThrowAlert({
                    message: result.data.toString(),
                    lifeTime: 10000
                } as NotificationModel));
            }
            else {
                dispatch(loadOpenedModel(id));
                dispatch(onThrowAlert({
                    message: "Cancellation requested. The model will leave the queue shortly",
                    lifeTime: 3000,
                    type: NotificationModelType.Warning
                } as NotificationModel));
            }
        }
        catch (error) {
            dispatch(onThrowAlert({
                message: (error as Error).message,
                lifeTime: 10000
            } as NotificationModel));
        }
        finally {
            setIsCancelConfirmOpen(false);
            setIsCancellationInProgress(false);
        }
    }, [dispatch])

    return {
        onRetrainModel,
        setIsRetrainConfirmOpen,
        onRecalculateMetrics,
        onCancelTraining,
        isRetrainConfirmOpen,
        isRetrainingInProgress,
        isCancelConfirmOpen,
        setIsCancelConfirmOpen,
        isCancellationInProgress
    }
}